import cx from 'classnames';
import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { LICENSE_CONCLUDED } from '../../../../../../../shared/featureFlags/flagNames';
import { LICENSING_ISSUE_TYPES, QUALITY_ISSUE_TYPES, VULN_ISSUE_TYPES } from '../../../../../../../shared/issuesV2';
import Locator from '../../../../../../../shared/Locator';
import { useIsFeatureEnabled } from '../../../../../../helpers/features';
import * as DependenciesV2Store from '../../../../../../redux/dependenciesV2';
import List from '../../../../../generic/List';
import PackageLabelsList from '../../../../../shared/packageLabels/PackageLabelsList';
import DependencyRootProjects from '../DependencyRootProjects';
import { isIssueOfActiveType } from '../helpers';
import type { Dependency } from '../../../../../../../shared/dependenciesV2';

import IssueSection from './IssueSection';
import ConcludedLicensingSection from './LicensingSections/ConcludedLicensingSection';
import LicensingSection from './LicensingSections/LicensingSection';

import styles from './DetailsRow.module.less';

type Props = {
  // Dependency data from API response
  dependency: Dependency;
};

/**
 * Displays issue and license details for a dependency
 */
const DetailsRow: VoidFunctionComponent<Props> = ({ dependency }) => {
  const isReleaseGroupScope = useSelector(DependenciesV2Store.selectors.getIsReleaseGroupScope);
  const isLicenseConcludedEnabled = useIsFeatureEnabled(LICENSE_CONCLUDED);

  const areLabelsPresent = (dependency.labels?.length ?? 0) > 0;
  const areIssuesPresent =
    dependency.issues.filter((issue) => isIssueOfActiveType(issue, LICENSING_ISSUE_TYPES)).length > 0 ||
    dependency.issues.filter((issue) => isIssueOfActiveType(issue, VULN_ISSUE_TYPES)).length > 0 ||
    dependency.issues.filter((issue) => isIssueOfActiveType(issue, QUALITY_ISSUE_TYPES)).length > 0;

  let rowCount: 1 | 2 | 3 | 4 = 1;
  if (areLabelsPresent) {
    // Accounts for the Package Labels row.
    rowCount++;
  }
  if (areIssuesPresent) {
    // Accounts for the Issues row.
    rowCount++;
  }
  if (isReleaseGroupScope) {
    // Accounts for the Projects row.
    rowCount++;
  }

  const rowCountString = rowCount.toString() as '1' | '2' | '3' | '4';
  const rowStyles = cx(styles.detailRowsContainer, { [styles[`${rowCountString}Row`]]: rowCount > 1 });

  return (
    <List.RowDetails>
      <div className={rowStyles}>
        {/* Licenses */}
        <div className={styles.detailRowLabel}>Licenses</div>
        <div className={styles.detailRowContent}>
          {isLicenseConcludedEnabled ? (
            <ConcludedLicensingSection
              licenses={dependency.licenses}
              declaredLicenses={dependency.declaredLicenses}
              concludedLicenses={dependency.conclusions?.scoped.licenses || []}
              issues={dependency.issues}
            />
          ) : (
            <LicensingSection
              licenses={dependency.licenses}
              declaredLicenses={dependency.declaredLicenses}
              issues={dependency.issues}
            />
          )}
        </div>
        {/* Labels */}
        {areLabelsPresent && (
          <>
            <div className={styles.detailRowLabel}>Labels</div>
            <div className={styles.detailRowContent}>
              <PackageLabelsList
                labels={dependency.labels}
                dependencyVersion={dependency.version}
                dependencyLocator={new Locator(dependency.locator)}
                labelPrefix=""
              />
            </div>
          </>
        )}
        {/* Issues */}
        {areIssuesPresent && (
          <>
            <div className={styles.detailRowLabel}>Issues</div>
            <div className={styles.detailRowContent}>
              <IssueSection dependency={dependency} />
            </div>
          </>
        )}
        {/* Projects */}
        {isReleaseGroupScope && (
          <>
            <div className={styles.detailRowLabel}>Projects</div>
            <div className={styles.detailRowContent}>
              <DependencyRootProjects dependency={dependency} rootProjects={[...dependency.rootProjects]} />
            </div>
          </>
        )}
      </div>
    </List.RowDetails>
  );
};

export default DetailsRow;
